import { useCallback, useEffect, useMemo, useState } from "react";
import { adminService } from "../../features/admin/service";
import type {
  Broadcast,
  NotifOpsDailyRow,
  NotifOpsDelivery,
  NotifOpsDeviceOwner,
  NotifOpsDuplicate,
  NotifOpsHealth,
  NotifOpsUserDevices,
  NotifOpsBroadcastRecipients,
} from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import { useAlertModal } from "../../components/admin/AlertModal";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

/**
 * Notification Ops — read-only diagnostics for the FCM + in-app stack.
 *
 * Separate from /admin/notifications (which SENDS). Opening this page must
 * never deliver anything, so the only write here is the explicit per-user
 * device prune, behind a confirm.
 *
 * The four tabs answer the four questions that duplicate-notification reports
 * always turn into: is it on, who has how many devices, what went out on which
 * day, and is anything being sent twice.
 */

type Tab = "health" | "devices" | "timeline" | "duplicates" | "broadcasts";

const TABS: { id: Tab; label: string; hint: string }[] = [
  { id: "health", label: "Health", hint: "Is push working right now" },
  { id: "devices", label: "Devices", hint: "Who holds how many registrations" },
  { id: "timeline", label: "Date-wise", hint: "What went out, and to whom" },
  { id: "duplicates", label: "Duplicates", hint: "Same thing sent more than once" },
  { id: "broadcasts", label: "Broadcasts", hint: "Per-recipient status for one broadcast" },
];

const card = "rounded-xl border border-white/10 bg-white/[0.03] p-4";
const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400";
const td = "px-3 py-2 text-sm text-slate-200 align-top";

const fmt = (v: string | null | undefined) => (v ? formatIST(v) : "—");
const pct = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${v}%`);

/** Today / N days ago as YYYY-MM-DD, for the date inputs. */
const dayString = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d.toISOString().slice(0, 10);
};


/**
 * Overlay for the device inspector. This used to render inline under the table,
 * which pushed the row you clicked off-screen on any list longer than a few
 * users — you lost your place every time you looked at an account.
 */
const Modal: React.FC<{ title: React.ReactNode; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children,
}) => {
  useBodyScrollLock();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 border-b border-white/10 px-5 py-4">
          <div className="min-w-0 flex-1">{title}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-lg border border-white/10 px-2.5 py-1 text-sm text-slate-400 hover:bg-white/5"
          >
            Esc
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 25;

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: "ok" | "warn" | "bad" | "plain";
}> = ({ label, value, sub, tone = "plain" }) => {
  const toneCls =
    tone === "ok" ? "text-emerald-300"
      : tone === "warn" ? "text-amber-300"
      : tone === "bad" ? "text-red-300"
      : "text-slate-100";
  return (
    <div className={card}>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

const Pill: React.FC<{ ok: boolean; okText: string; badText: string }> = ({ ok, okText, badText }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
      ok
        ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
        : "border-red-500/30 bg-red-500/15 text-red-300"
    }`}
  >
    {ok ? okText : badText}
  </span>
);

// ── Health ──────────────────────────────────────────────────────────────────
const HealthTab: React.FC = () => {
  const [data, setData] = useState<NotifOpsHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminService.getNotifOpsHealth());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (error) return <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">{error}</div>;
  if (!data) return null;

  const { fcm, reach, device_tokens: dt, in_app: inApp, idempotency } = data;
  const configured = fcm.enabled_in_config && fcm.credentials_present;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Pill ok={configured} okText="FCM configured" badText="FCM NOT configured" />
        <Pill
          ok={fcm.firebase_initialised_in_this_worker}
          okText="Firebase SDK loaded in this worker"
          badText="Firebase SDK not yet loaded in this worker"
        />
        <span className="text-xs text-slate-500">
          checked {fmt(data.checked_at)}
        </span>
        <button
          onClick={() => void load()}
          className="ml-auto rounded-lg border border-white/10 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"
        >
          Refresh
        </button>
      </div>

      {/*
        The SDK loads lazily on the first send, and each Gunicorn worker has its
        own process. "Not loaded" on a worker that has not sent yet is normal —
        it is not an outage, so it must not be styled as one.
      */}
      {!fcm.firebase_initialised_in_this_worker && configured && (
        <p className="text-xs text-slate-500">
          The Firebase SDK initialises lazily on a worker's first send, and each
          worker is a separate process — this flag reflects the one worker that
          served this request, not the fleet.
        </p>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-300">Reach — who FCM can physically deliver to</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            label="Reachable users"
            value={`${reach.users_with_active_token} / ${reach.total_users}`}
            sub={`${reach.reachable_pct}% own at least one active device token`}
            tone={reach.reachable_pct < 25 ? "bad" : reach.reachable_pct < 60 ? "warn" : "ok"}
          />
          <Stat
            label="Unreachable users"
            value={reach.unreachable_users}
            sub="A push to these can only ever land in the bell"
            tone={reach.unreachable_users > 0 ? "warn" : "ok"}
          />
          <Stat label="Push attempts (24h)" value={fcm.sends_last_24h} />
          <Stat
            label="Delivered (24h)"
            value={fcm.delivered_last_24h}
            sub={`last success ${fmt(fcm.last_successful_send_at)}`}
            tone={fcm.sends_last_24h > 0 && fcm.delivered_last_24h === 0 ? "bad" : "ok"}
          />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-300">Device registrations</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Active token rows" value={dt.rows_active} sub={`${dt.rows_total} rows stored in total`} />
          <Stat
            label="Users over the cap"
            value={dt.users_over_cap}
            sub={`cap is ${dt.cap_per_user} active devices per user`}
            tone={dt.users_over_cap > 0 ? "bad" : "ok"}
          />
          <Stat
            label="Active but stale"
            value={dt.active_rows_past_stale_window}
            sub={`not seen for over ${dt.stale_days} days`}
            tone={dt.active_rows_past_stale_window > 0 ? "warn" : "ok"}
          />
          <Stat
            label="Idempotency claims"
            value={idempotency.active_delivery_claims}
            sub="Live keys currently blocking a repeat send"
          />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-300">In-app bell</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Stored notifications" value={inApp.rows_total} />
          <Stat label="Created (24h)" value={inApp.created_last_24h} />
          <Stat label="Unread" value={inApp.unread_total} />
        </div>
      </div>
    </div>
  );
};

// ── Devices ─────────────────────────────────────────────────────────────────
const DevicesTab: React.FC = () => {
  const { alertModal, showAlert } = useAlertModal();
  const [confirmTarget, setConfirmTarget] = useState<{ userId: string; activeCount: number } | null>(null);
  const [rows, setRows] = useState<NotifOpsDeviceOwner[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [cap, setCap] = useState(5);
  const [minActive, setMinActive] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<NotifOpsUserDevices | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pruning, setPruning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Server-side: the endpoint pages the aggregate itself, so a deployment
      // with thousands of registered users never ships the whole list.
      const res = await adminService.getNotifOpsDevices({
        min_active: minActive, limit: pageSize, offset: page * pageSize,
      });
      setRows(res.items);
      setTotal(res.total);
      setCap(res.cap_per_user);
    } catch (e: any) {
      showAlert(e?.response?.data?.detail || e?.message || "Failed to load devices",
        { title: "Failed to load devices", variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [minActive, page, pageSize, showAlert]);

  useEffect(() => { void load(); }, [load]);

  // Changing the filter re-pages from the start; staying on page 7 of a
  // narrower result set shows an empty table and reads as "no data".
  const applyMinActive = (n: number) => { setMinActive(Math.max(1, n)); setPage(0); };

  const openDetail = async (userId: string) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await adminService.getNotifOpsUserDevices(userId));
    } catch (e: any) {
      showAlert(e?.response?.data?.detail || e?.message || "Failed to load device list",
        { title: "Failed to load device list", variant: "error" });
      setDetailLoading(false);
      return;
    }
    setDetailLoading(false);
  };

  const runPrune = async (userId: string) => {
    setPruning(true);
    try {
      const res = await adminService.pruneNotifOpsUserDevices(userId);
      setConfirmTarget(null);
      showAlert(
        `${res.before} active → ${res.after} active (${res.deactivated} retired). ` +
          `This user now receives ${res.after} push${res.after === 1 ? "" : "es"} per notification.`,
        { title: "Pruned", variant: "info" },
      );
      await openDetail(userId);
      await load();
    } catch (e: any) {
      setConfirmTarget(null);
      showAlert(e?.response?.data?.detail || e?.message || "Prune failed",
        { title: "Prune failed", variant: "error" });
    } finally {
      setPruning(false);
    }
  };

  const showModal = detailLoading || Boolean(detail);

  return (
    <div className="space-y-4">
      {alertModal}
      {confirmTarget && (
        <ConfirmModal
          title="Prune device registrations?"
          description={
            `Keeps the ${cap} most recently seen of ${confirmTarget.activeCount} active ` +
            `registrations and retires the rest. Rows are deactivated, not deleted — a real ` +
            `device that comes back re-registers on its next login.`
          }
          confirmLabel={`Prune to ${cap}`}
          confirmClass="bg-amber-600 hover:bg-amber-500"
          irreversible={false}
          busy={pruning}
          busyLabel="Pruning…"
          onConfirm={() => void runPrune(confirmTarget.userId)}
          onCancel={() => setConfirmTarget(null)}
        />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-400">
          Show users with at least
          <input
            type="number"
            min={1}
            value={minActive}
            onChange={(e) => applyMinActive(Number(e.target.value) || 1)}
            className="mx-2 w-16 rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-slate-100"
          />
          active registration(s)
        </label>
        <button
          onClick={() => applyMinActive(cap + 1)}
          className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-300 hover:bg-amber-500/20"
        >
          Show only over-cap ({">"}{cap})
        </button>
        <span className="ml-auto text-sm text-slate-400">{total} user(s)</span>
        <RefreshButton onRefresh={load} />
      </div>

      <p className="text-xs text-slate-500">
        Every active registration costs one push. A user whose registration count
        far exceeds their real device count is a client minting a new device id
        per install — the duplicate arrives on the phone even though the backend
        sent the notification once.
      </p>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full divide-y divide-white/5">
              <thead className="bg-white/[0.02]">
                <tr>
                  <th className={th}>User</th>
                  <th className={th}>Role</th>
                  <th className={th}>Active</th>
                  <th className={th}>Device ids</th>
                  <th className={th}>Platforms</th>
                  <th className={th}>Last seen</th>
                  <th className={th}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {rows.map((r) => (
                  <tr key={r.user_id} className={r.over_cap ? "bg-red-500/[0.06]" : undefined}>
                    <td className={td}>
                      <div className="font-medium">{r.name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </td>
                    <td className={td}>{r.role}</td>
                    <td className={td}>
                      <span className={r.over_cap ? "font-semibold text-red-300" : ""}>{r.active_tokens}</span>
                      {r.over_cap && <div className="text-[11px] text-red-400">{r.pushes_per_notification} pushes each time</div>}
                    </td>
                    <td className={td}>{r.distinct_device_ids}</td>
                    <td className={td}>{r.platforms.join(", ") || "—"}</td>
                    <td className={td}>{fmt(r.last_seen_at)}</td>
                    <td className={td}>
                      <button
                        onClick={() => void openDetail(r.user_id)}
                        className="rounded-lg border border-white/10 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5"
                      >
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td className={`${td} text-center text-slate-500`} colSpan={7}>No users match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
            variant="panel"
          />
        </>
      )}

      {showModal && (
        <Modal
          onClose={() => setDetail(null)}
          title={
            detail ? (
              <>
                <div className="truncate font-medium text-slate-100">{detail.name || detail.user_id}</div>
                <div className="truncate text-xs text-slate-500">{detail.email}</div>
                <div className="mt-1 text-sm text-slate-400">
                  {detail.active_count} active · {detail.inactive_count} retired ·{" "}
                  {detail.distinct_active_device_ids} distinct device ids ·{" "}
                  <span className={detail.would_receive_pushes > 1 ? "text-amber-300" : ""}>
                    {detail.would_receive_pushes} push{detail.would_receive_pushes === 1 ? "" : "es"} per notification
                  </span>
                </div>
              </>
            ) : (
              <span className="text-sm text-slate-400">Loading devices…</span>
            )
          }
        >
          {detailLoading || !detail ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <>
              {detail.over_cap && (
                <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                  <p className="flex-1 text-xs text-amber-200">
                    Over the cap of {detail.cap_per_user}. Every extra registration is one
                    more copy of every notification on this person's phone.
                  </p>
                  <button
                    disabled={pruning}
                    onClick={() => setConfirmTarget({ userId: detail.user_id, activeCount: detail.active_count })}
                    className="shrink-0 rounded-lg border border-amber-500/40 bg-amber-500/20 px-3 py-1.5 text-sm text-amber-100 hover:bg-amber-500/30 disabled:opacity-50"
                  >
                    {pruning ? "Pruning…" : `Prune to ${detail.cap_per_user}`}
                  </button>
                </div>
              )}
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <table className="min-w-full divide-y divide-white/5">
                  <thead className="sticky top-0 bg-slate-900">
                    <tr>
                      <th className={th}>Device id</th>
                      <th className={th}>Type</th>
                      <th className={th}>App</th>
                      <th className={th}>Status</th>
                      <th className={th}>Token</th>
                      <th className={th}>Registered</th>
                      <th className={th}>Last seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {detail.items.map((d) => (
                      <tr key={d.id}>
                        <td className={`${td} font-mono text-xs`}>{d.device_id || <span className="text-slate-600">none</span>}</td>
                        <td className={td}>{d.platform || "—"}</td>
                        <td className={td}>{d.app_version || "—"}</td>
                        <td className={td}>
                          {d.active ? (
                            <span className="text-emerald-300">active</span>
                          ) : (
                            <span className="text-slate-500">retired</span>
                          )}
                          {d.stale && <div className="text-[11px] text-amber-400">stale</div>}
                        </td>
                        <td className={`${td} font-mono text-xs text-slate-500`}>{d.token_masked}</td>
                        <td className={td}>{fmt(d.created_at)}</td>
                        <td className={td}>
                          {fmt(d.last_seen_at)}
                          {d.days_since_seen !== null && (
                            <div className="text-[11px] text-slate-500">{d.days_since_seen}d ago</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
};

// ── Date-wise timeline ──────────────────────────────────────────────────────
const TimelineTab: React.FC = () => {
  const [from, setFrom] = useState(dayString(29));
  const [to, setTo] = useState(dayString(0));
  const [daily, setDaily] = useState<NotifOpsDailyRow[]>([]);
  const [feed, setFeed] = useState<NotifOpsDelivery[]>([]);
  const [channel, setChannel] = useState<"all" | "fcm" | "in_app">("all");
  const [userId, setUserId] = useState("");
  const [feedPage, setFeedPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // A new query always starts at page 1 — keeping the old offset against a
    // different result set shows an empty table that reads as "no data".
    setFeedPage(0);
    try {
      const params: Record<string, any> = { date_from: from, date_to: to };
      const [d, f] = await Promise.all([
        adminService.getNotifOpsDaily(params),
        adminService.getNotifOpsDeliveries({
          ...params, channel, limit: 200, ...(userId.trim() ? { user_id: userId.trim() } : {}),
        }),
      ]);
      setDaily(d.items);
      setFeed(f.items);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [from, to, channel, userId]);

  useEffect(() => { void load(); }, [load]);

  const totals = useMemo(
    () => daily.reduce(
      (a, r) => ({
        attempted: a.attempted + r.fcm_attempted,
        delivered: a.delivered + r.fcm_delivered,
        bell: a.bell + r.in_app_created,
      }),
      { attempted: 0, delivered: 0, bell: 0 },
    ),
    [daily],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-400">
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="mt-1 block rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-sm text-slate-100" />
        </label>
        <label className="text-xs text-slate-400">
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="mt-1 block rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-sm text-slate-100" />
        </label>
        <label className="text-xs text-slate-400">
          Channel
          <select value={channel} onChange={(e) => setChannel(e.target.value as any)}
            className="mt-1 block rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-sm text-slate-100">
            <option value="all">All</option>
            <option value="fcm">FCM push</option>
            <option value="in_app">In-app bell</option>
          </select>
        </label>
        <label className="text-xs text-slate-400">
          User id (optional)
          <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="6a30fd…"
            className="mt-1 block w-48 rounded-lg border border-white/10 bg-slate-900 px-2 py-1 font-mono text-sm text-slate-100" />
        </label>
        <button onClick={() => void load()}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-200 hover:bg-white/10">
          Apply
        </button>
      </div>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
      {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Push attempted" value={totals.attempted} />
            <Stat
              label="Push delivered"
              value={totals.delivered}
              sub={totals.attempted ? `${Math.round((100 * totals.delivered) / totals.attempted)}% of attempts` : undefined}
              tone={totals.attempted > 0 && totals.delivered === 0 ? "bad" : "ok"}
            />
            <Stat label="Bell rows created" value={totals.bell} />
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full divide-y divide-white/5">
              <thead className="bg-white/[0.02]">
                <tr>
                  <th className={th}>Date</th>
                  <th className={th}>Push attempted</th>
                  <th className={th}>Delivered</th>
                  <th className={th}>Failed</th>
                  <th className={th}>Rate</th>
                  <th className={th}>Recipients</th>
                  <th className={th}>Bell created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {daily.map((r) => (
                  <tr key={r.date} className={r.fcm_attempted > 0 && r.fcm_delivered === 0 ? "bg-red-500/[0.06]" : undefined}>
                    <td className={`${td} font-medium`}>{r.date}</td>
                    <td className={td}>{r.fcm_attempted}</td>
                    <td className={td}>{r.fcm_delivered}</td>
                    <td className={td}>{r.fcm_failed || "—"}</td>
                    <td className={td}>{pct(r.delivery_rate_pct)}</td>
                    <td className={td}>{r.recipients}</td>
                    <td className={td}>{r.in_app_created}</td>
                  </tr>
                ))}
                {daily.length === 0 && (
                  <tr><td className={`${td} text-center text-slate-500`} colSpan={7}>Nothing sent in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <h3 className="pt-2 text-sm font-semibold text-slate-300">
            Individual deliveries <span className="font-normal text-slate-500">(newest first, capped at 200)</span>
          </h3>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full divide-y divide-white/5">
              <thead className="bg-white/[0.02]">
                <tr>
                  <th className={th}>When</th>
                  <th className={th}>Channel</th>
                  <th className={th}>Recipient</th>
                  <th className={th}>Type</th>
                  <th className={th}>Title</th>
                  <th className={th}>Push</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {feed.slice(feedPage * PAGE_SIZE, (feedPage + 1) * PAGE_SIZE).map((d) => (
                  <tr key={`${d.source}:${d.id}`}>
                    <td className={`${td} whitespace-nowrap`}>{fmt(d.at)}</td>
                    <td className={td}>
                      <span className="text-xs">{d.channel === "fcm" ? "push" : "bell"}</span>
                      <div className="text-[11px] text-slate-600">{d.source}</div>
                    </td>
                    <td className={td}>
                      <div>{d.user?.name || d.user_id}</div>
                      <div className="text-xs text-slate-500">{d.user?.email}</div>
                    </td>
                    <td className={td}>{d.type || "—"}</td>
                    <td className={`${td} max-w-md`}>{d.title || "—"}</td>
                    <td className={td}>
                      {d.fcm_sent === null ? <span className="text-slate-600">n/a</span>
                        : d.fcm_sent ? <span className="text-emerald-300">sent</span>
                        : <span className="text-red-300">failed</span>}
                    </td>
                  </tr>
                ))}
                {feed.length === 0 && (
                  <tr><td className={`${td} text-center text-slate-500`} colSpan={6}>No deliveries in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {feed.length > PAGE_SIZE && (
            <Pagination
              page={feedPage}
              pageSize={PAGE_SIZE}
              total={feed.length}
              onPageChange={setFeedPage}
              variant="panel"
            />
          )}
        </>
      )}
    </div>
  );
};

// ── Duplicates ──────────────────────────────────────────────────────────────
const DuplicatesTab: React.FC = () => {
  const [from, setFrom] = useState(dayString(29));
  const [to, setTo] = useState(dayString(0));
  const [minCount, setMinCount] = useState(2);
  const [rows, setRows] = useState<NotifOpsDuplicate[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPage(0);
    try {
      const res = await adminService.getNotifOpsDuplicates({
        date_from: from, date_to: to, min_count: minCount, limit: 100,
      });
      setRows(res.items);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [from, to, minCount]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-400">
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="mt-1 block rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-sm text-slate-100" />
        </label>
        <label className="text-xs text-slate-400">
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="mt-1 block rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-sm text-slate-100" />
        </label>
        <label className="text-xs text-slate-400">
          Repeats of at least
          <input type="number" min={2} value={minCount}
            onChange={(e) => setMinCount(Math.max(2, Number(e.target.value) || 2))}
            className="mt-1 block w-20 rounded-lg border border-white/10 bg-slate-900 px-2 py-1 text-sm text-slate-100" />
        </label>
        <button onClick={() => void load()}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-200 hover:bg-white/10">
          Apply
        </button>
      </div>

      <p className="text-xs text-slate-500">
        One row = one user told the same thing more than once on the same day.
        This is a server-side repeat. A user receiving one notification on several
        phones is a different problem and shows up under Devices instead.
      </p>

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
      {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full divide-y divide-white/5">
            <thead className="bg-white/[0.02]">
              <tr>
                <th className={th}>Repeats</th>
                <th className={th}>Day</th>
                <th className={th}>Recipient</th>
                <th className={th}>Type</th>
                <th className={th}>Subject</th>
                <th className={th}>Window</th>
                <th className={th}>Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((r, i) => (
                <tr key={`${r.source}:${r.day}:${r.user_id}:${i}`} className={r.count >= 10 ? "bg-red-500/[0.06]" : undefined}>
                  <td className={td}>
                    <span className={r.count >= 10 ? "text-lg font-semibold text-red-300" : "font-medium"}>{r.count}</span>
                  </td>
                  <td className={td}>{r.day}</td>
                  <td className={td}>
                    <div>{r.user?.name || r.user_id}</div>
                    <div className="text-xs text-slate-500">{r.user?.email}</div>
                  </td>
                  <td className={td}>{r.type || "—"}</td>
                  <td className={`${td} max-w-xs truncate font-mono text-xs`}>{r.subject_id || "—"}</td>
                  <td className={td}>
                    {r.span_seconds === null ? "—"
                      : r.span_seconds < 120 ? `${r.span_seconds}s`
                      : `${Math.round(r.span_seconds / 60)}min`}
                    <div className="text-[11px] text-slate-600">{fmt(r.first_at)}</div>
                  </td>
                  <td className={`${td} text-xs text-slate-500`}>{r.source}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className={`${td} text-center text-emerald-300`} colSpan={7}>
                    No duplicates in this range — every notification went out once.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {!loading && rows.length > PAGE_SIZE && (
        <Pagination page={page} pageSize={PAGE_SIZE} total={rows.length} onPageChange={setPage} variant="panel" />
      )}
    </div>
  );
};


// ── Broadcasts — per-recipient status ───────────────────────────────────────
const BroadcastsTab: React.FC = () => {
  const [list, setList] = useState<Broadcast[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<NotifOpsBroadcastRecipients | null>(null);
  const [filter, setFilter] = useState<"all" | "delivered" | "unreachable" | "unread">("all");
  const [rPage, setRPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const res = await adminService.getBroadcasts({ page: 1, page_size: 25 });
        setList((res.items as Broadcast[]) || []);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load broadcasts");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let cancelled = false;
    void (async () => {
      setDetailLoading(true);
      setError(null);
      try {
        const res = await adminService.getNotifOpsBroadcastRecipients(selected, {
          status: filter, limit: PAGE_SIZE, offset: rPage * PAGE_SIZE,
        });
        if (!cancelled) setDetail(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load recipients");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected, filter, rPage]);

  return (
    <div className="space-y-4">
      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}

      {loading ? <div className="flex justify-center py-16"><Spinner /></div> : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full divide-y divide-white/5">
            <thead className="bg-white/[0.02]">
              <tr>
                <th className={th}>Title</th>
                <th className={th}>Sent</th>
                <th className={th}>Mode</th>
                <th className={th}>Audience</th>
                <th className={th}>Push reachable</th>
                <th className={th}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {list.map((b: any) => (
                <tr key={b.id} className={selected === b.id ? "bg-white/[0.05]" : undefined}>
                  <td className={`${td} max-w-xs truncate`}>{b.title}</td>
                  <td className={td}>{fmt(b.sent_at || b.scheduled_at)}</td>
                  <td className={td}>{b.delivery_mode || "all"}</td>
                  <td className={td}>{b.reach_count ?? b.recipient_count ?? "—"}</td>
                  <td className={td}>{b.push_count ?? "—"}</td>
                  <td className={td}>
                    <button
                      onClick={() => { setRPage(0); setSelected(selected === b.id ? null : b.id); }}
                      className="rounded-lg border border-white/10 px-2.5 py-1 text-xs text-slate-300 hover:bg-white/5"
                    >
                      {selected === b.id ? "Hide" : "Who got it"}
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td className={`${td} text-center text-slate-500`} colSpan={6}>No broadcasts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
          {detailLoading ? <div className="flex justify-center py-8"><Spinner /></div> : detail && (
            <>
              {!detail.summary.traceable ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
                  This broadcast cannot be traced per recipient. Its notifications were
                  written before the broadcast id was stamped into them, so there is no
                  stored link from a recipient back to this broadcast. Broadcasts sent
                  from now on are fully traceable.
                </div>
              ) : (
                <>
                  <div className="mb-3 grid gap-3 sm:grid-cols-4">
                    <Stat label="Recipients traced" value={detail.summary.bell_rows_found} />
                    <Stat
                      label="Push reachable"
                      value={detail.summary.push_reachable_recipients}
                      sub="had at least one active device"
                      tone={detail.summary.push_reachable_recipients === 0 ? "bad" : "ok"}
                    />
                    <Stat
                      label="Bell only"
                      value={detail.summary.bell_only_recipients}
                      sub="no device — push could not land"
                      tone={detail.summary.bell_only_recipients > 0 ? "warn" : "ok"}
                    />
                    <Stat label="Opened it" value={detail.summary.read_recipients} />
                  </div>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {(["all", "delivered", "unreachable", "unread"] as const).map((f) => (
                      <button
                        key={f}
                        onClick={() => { setFilter(f); setRPage(0); }}
                        className={`rounded-lg px-2.5 py-1 text-xs ${
                          filter === f ? "bg-white/10 text-slate-100" : "text-slate-400 hover:bg-white/5"
                        }`}
                      >
                        {f === "delivered" ? "push reachable" : f === "unreachable" ? "bell only" : f}
                      </button>
                    ))}
                    <span className="ml-auto text-xs text-slate-500">{detail.total} row(s)</span>
                  </div>
                  <div className="max-h-96 overflow-auto rounded-lg border border-white/10">
                    <table className="min-w-full divide-y divide-white/5">
                      <thead className="sticky top-0 bg-slate-900">
                        <tr>
                          <th className={th}>Recipient</th>
                          <th className={th}>Bell</th>
                          <th className={th}>Opened</th>
                          <th className={th}>Devices</th>
                          <th className={th}>Pushes sent</th>
                          <th className={th}>Platforms</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {detail.items.map((r) => (
                          <tr key={r.user_id} className={r.pushes_attempted > 1 ? "bg-amber-500/[0.06]" : undefined}>
                            <td className={td}>
                              <div>{r.name || r.user_id}</div>
                              <div className="text-xs text-slate-500">{r.email}</div>
                            </td>
                            <td className={td}>{fmt(r.bell_created_at)}</td>
                            <td className={td}>
                              {r.bell_read ? <span className="text-emerald-300">yes</span> : <span className="text-slate-500">no</span>}
                            </td>
                            <td className={td}>{r.active_devices}</td>
                            <td className={td}>
                              {r.pushes_attempted === 0
                                ? <span className="text-amber-300">none — unreachable</span>
                                : r.pushes_attempted}
                            </td>
                            <td className={td}>{r.platforms.join(", ") || "—"}</td>
                          </tr>
                        ))}
                        {detail.items.length === 0 && (
                          <tr><td className={`${td} text-center text-slate-500`} colSpan={6}>No rows for this filter.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {detail.total > PAGE_SIZE && (
                    <Pagination
                      page={rPage}
                      pageSize={PAGE_SIZE}
                      total={detail.total}
                      onPageChange={setRPage}
                      variant="panel"
                    />
                  )}
                  <p className="mt-2 text-xs text-slate-500">
                    "Pushes sent" counts the device registrations FCM was asked to deliver to.
                    More than one means that person's phone can show the same broadcast several
                    times — check the Devices tab for that account.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────
const AdminNotificationOpsPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>("health");

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100">Notification Ops</h1>
        <p className="mt-1 text-sm text-slate-400">
          Diagnostics for FCM push and the in-app bell. Read-only — nothing on this
          page sends a notification.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            title={t.hint}
            className={`rounded-lg px-3 py-1.5 text-sm transition ${
              tab === t.id
                ? "bg-white/10 font-medium text-slate-100"
                : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "health" && <HealthTab />}
      {tab === "devices" && <DevicesTab />}
      {tab === "timeline" && <TimelineTab />}
      {tab === "duplicates" && <DuplicatesTab />}
      {tab === "broadcasts" && <BroadcastsTab />}
    </div>
  );
};

export default AdminNotificationOpsPage;
